import React, { useCallback, useMemo } from 'react';
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Checkbox from 'js/components/Forms/Checkbox';
import {
  QuestionSelectionProvider,
  useQuestionSelection,
} from './QuestionSelectionContext';
import QuestionOptionCard from './QuestionOptionCard';
import { pluralize } from 'js/utils/string';
import { isScoredType } from 'js/utils/question-set';
import styles from './RubricQuestionPickerModal.module.scss';

export default function BrowseQuestionsPhase({
  activeAssessment,
  onBackToAssessments,
  onAddQuestions,
  onAddQuestionsWithSets,
  onClose = () => {},
  /** `addQuestions` | `addSource` — source mode: one question only, single primary action. */
  pickerMode = 'addQuestions',
}) {
  if (!activeAssessment) {
    return null;
  }

  const selectionMode = pickerMode === 'addSource' ? 'single' : 'multi';

  return (
    <QuestionSelectionProvider
      questionSets={activeAssessment.questionSets}
      selectionMode={selectionMode}
    >
      <BrowseQuestionsPhaseContent
        activeAssessment={activeAssessment}
        onBackToAssessments={onBackToAssessments}
        onAddQuestions={onAddQuestions}
        onAddQuestionsWithSets={onAddQuestionsWithSets}
        onClose={onClose}
        pickerMode={pickerMode}
      />
    </QuestionSelectionProvider>
  );
}

function BrowseQuestionsPhaseContent({
  activeAssessment,
  onBackToAssessments,
  onAddQuestions,
  onAddQuestionsWithSets,
  onClose,
  pickerMode = 'addQuestions',
}) {
  const {
    selectedCount,
    selectedQuestions,
    totalQuestionCount,
    isAllQuestionsSelected,
    isQuestionSelected,
    toggleQuestion,
    toggleQuestionSet,
    selectAllQuestions,
    getQuestionSetSelectionState,
    clearSelection,
  } = useQuestionSelection();

  const handleAddQuestions = useCallback(() => {
    onAddQuestions?.(selectedQuestions);
  }, [onAddQuestions, selectedQuestions]);

  const handleAddQuestionsWithSets = useCallback(() => {
    onAddQuestionsWithSets?.(selectedQuestions);
  }, [onAddQuestionsWithSets, selectedQuestions]);

  const handlePickSource = useCallback(
    (question) => {
      if (!isScoredType(question?.type ?? question?.question_type)) {
        return;
      }
      onAddQuestions?.([question]);
    },
    [onAddQuestions]
  );

  const selectedParentSetCount = useMemo(() => {
    return new Set(
      selectedQuestions.map((question) => question.question_set_key)
    ).size;
  }, [selectedQuestions]);

  const isAddSourceMode = pickerMode === 'addSource';

  return (
    <>
      <ModalHeader toggle={onClose}>
        {isAddSourceMode ? 'Add a source' : 'Choose source questions'}
      </ModalHeader>
      <ModalBody>
        <div className={styles['picker-phase-browse']}>
          <div className={styles['picker-selected-bar']}>
            <div className={styles['picker-selected-text']}>
              <div className={styles['assessment-name']}>
                {activeAssessment.primaryName}
              </div>
            </div>
            <div className={styles['picker-browse-actions']}>
              {!isAddSourceMode && totalQuestionCount > 0 && (
                <Button
                  type="button"
                  color="link"
                  className={styles['picker-change-assessment']}
                  onClick={
                    isAllQuestionsSelected ? clearSelection : selectAllQuestions
                  }
                >
                  {isAllQuestionsSelected ? 'Clear all' : 'Select all'}
                </Button>
              )}
              <Button
                type="button"
                color="link"
                className={styles['picker-change-assessment']}
                onClick={onBackToAssessments}
              >
                Change assessment
              </Button>
            </div>
          </div>

          <div className={styles['picker-scroll-area']}>
            {activeAssessment.questionSets.map((set) => (
              <div key={set.key} className={styles['picker-question-set']}>
                <div className={styles['picker-question-set-title']}>
                  {isAddSourceMode ? (
                    <>
                      <span></span>
                      <span className={styles['picker-question-set-label']}>
                        {set.name || set.key}
                      </span>
                    </>
                  ) : (set.questions ?? []).some((q) =>
                      isScoredType(q?.type ?? q?.question_type)
                    ) ? (
                    <Checkbox
                      checked={getQuestionSetSelectionState(set).checked}
                      indeterminate={
                        getQuestionSetSelectionState(set).indeterminate
                      }
                      onChange={() => toggleQuestionSet(set)}
                    >
                      {set.name || set.key}
                    </Checkbox>
                  ) : (
                    <>
                      <span></span>
                      <span className={styles['picker-question-set-label']}>
                        {set.name || set.key}
                      </span>
                    </>
                  )}
                </div>
                <ul className={styles['picker-question-list']}>
                  {set.questions.map((q) => (
                    <li key={`${q.question_set_key}-${q.key}`}>
                      <QuestionOptionCard
                        pickerMode={pickerMode}
                        question={q}
                        checked={isQuestionSelected(q)}
                        onToggle={() => toggleQuestion(q)}
                        onAddSource={() => handlePickSource(q)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </ModalBody>
      {!isAddSourceMode && selectedCount > 0 && (
        <ModalFooter>
          <div>
            <Button
              color="primary"
              onClick={handleAddQuestions}
              className="mr-2"
            >
              Add {pluralize(selectedCount, 'question', 'questions')}
            </Button>
            {!isAddSourceMode && onAddQuestionsWithSets && (
              <Button
                color="primary"
                outline
                onClick={handleAddQuestionsWithSets}
              >
                Add {pluralize(selectedCount, 'question', 'questions')} and{' '}
                {pluralize(selectedParentSetCount, 'set', 'sets')}
              </Button>
            )}
          </div>
        </ModalFooter>
      )}
    </>
  );
}
